import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { TIPOLOGIE_LAVORAZIONE } from '@/lib/f1f2';
import { WORKFLOW_STATES } from '@/lib/workflow';
import { CATEGORIE_RIFIUTO } from '@/lib/rejectNote';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { X, SlidersHorizontal, ChevronDown, ChevronUp, Search, Info } from 'lucide-react';

const STATO_LABELS = {
  [WORKFLOW_STATES.NON_AVVIATO]: 'Non Avviato',
  [WORKFLOW_STATES.IN_CORSO]: 'In Corso',
  [WORKFLOW_STATES.APPROVAZIONE_HQ]: 'Documenti approvati',
  [WORKFLOW_STATES.CHIUSO]: 'Chiuso',
  [WORKFLOW_STATES.RIFIUTATO]: 'Rifiutato',
  [WORKFLOW_STATES.RIFIUTATO_DITTA]: 'Rifiutato dalla ditta',
};

const emptyFilters = {
  search: '',
  studioProgettazione: '',
  tipologiaLavorazione: '',
  ditta: '',
  workflowStatus: '',
  fornitoreIsInternalHq: '',
  categoriaRifiuto: '',
  minErrori: '',
  periodoDa: '',
  periodoA: '',
};

const MIN_ERRORI_LABELS = {
  zero: 'Nessun errore',
  1: 'Almeno 1',
  2: 'Almeno 2',
  3: 'Almeno 3',
  4: 'Almeno 4',
};

function useActiveFilterCount(filters, showSearch) {
  let count = 0;
  if (showSearch && filters.search) count++;
  if (filters.studioProgettazione) count++;
  if (filters.tipologiaLavorazione) count++;
  if (filters.ditta) count++;
  if (filters.workflowStatus) count++;
  if (filters.fornitoreIsInternalHq) count++;
  if (filters.categoriaRifiuto) count++;
  if (filters.minErrori) count++;
  if (filters.periodoDa || filters.periodoA) count++;
  return count;
}

export default function GlobalFilters({ showSearch = false }) {
  const { role } = useAuth();
  const { filters, setFilters, uniqueStudi, uniqueDitte } = useData();
  const [open, setOpen] = useState(false);
  const activeCount = useActiveFilterCount(filters, showSearch);
  const showStudioFilter = role === 'hq' || role === 'fastweb';
  // La Ditta vede solo le pratiche della propria ditta (auto-filtrate dal DataContext):
  // nasconde il dropdown Ditta come il Fornitore non vede il dropdown Studio Progettazione.
  const showDittaFilter = role !== 'ditta';

  const updateFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const handleReset = () => {
    setFilters(emptyFilters);
  };

  const activePreview = [
    filters.search && `Cerca: "${filters.search}"`,
    filters.studioProgettazione && `Studio: ${filters.studioProgettazione}`,
    filters.tipologiaLavorazione && `Tipo Intervento: ${filters.tipologiaLavorazione}`,
    filters.ditta && `Ditta: ${filters.ditta}`,
    filters.workflowStatus && `Stato: ${STATO_LABELS[filters.workflowStatus] || filters.workflowStatus}`,
    filters.fornitoreIsInternalHq === 'yes' && 'Controllore: Fastweb',
    filters.fornitoreIsInternalHq === 'no' && 'Controllore: HQ',
    filters.categoriaRifiuto && `Cat. Rifiuto: ${filters.categoriaRifiuto}`,
    filters.minErrori && `Errori: ${MIN_ERRORI_LABELS[filters.minErrori] || filters.minErrori}`,
    (filters.periodoDa || filters.periodoA) && `Periodo: ${filters.periodoDa || '...'} → ${filters.periodoA || '...'}`,
  ].filter(Boolean).join(' · ');

  return (
    <div data-testid="global-filters">
      {/* Toggle bar */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-white rounded-lg border border-fw-border border-l-4 border-l-fw-yellow shadow-sm hover:bg-fw-light transition-colors duration-150"
      >
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4 text-fw-yellow" />
          <span className="text-sm font-medium text-fw-dark">Filtri</span>
          {activeCount > 0 && (
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-fw-yellow text-fw-dark text-xs font-bold">
              {activeCount}
            </span>
          )}
          {activeCount > 0 && !open && (
            <span className="text-xs text-fw-subtle ml-1 hidden sm:inline">
              {activePreview}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {activeCount > 0 && (
            <span
              role="button"
              onClick={(e) => { e.stopPropagation(); handleReset(); }}
              className="text-xs text-fw-subtle hover:text-fw-red transition-colors"
            >
              Reset
            </span>
          )}
          {open ? <ChevronUp className="w-4 h-4 text-fw-subtle" /> : <ChevronDown className="w-4 h-4 text-fw-subtle" />}
        </div>
      </button>

      {/* Expandable filter panel */}
      {open && (
        <div className="mt-2 flex flex-wrap items-end gap-3 p-4 bg-white rounded-lg border border-fw-border shadow-sm animate-in fade-in slide-in-from-top-1 duration-200">
          {showSearch && (
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-fw-subtle">Cerca</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-fw-subtle" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => updateFilter('search', e.target.value)}
                  placeholder="ID Lav., Cod. Sito, Nome Sito..."
                  className="h-9 w-56 rounded-md border border-fw-border-strong pl-8 pr-3 text-sm shadow-sm focus:border-fw-yellow focus:ring-1 focus:ring-fw-yellow outline-none"
                  data-testid="search-input"
                />
              </div>
            </div>
          )}

          {/* Filtro Tipo Lavorazione */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-fw-subtle">Tipo di Intervento</label>
            <Select value={filters.tipologiaLavorazione || '__all__'} onValueChange={(v) => updateFilter('tipologiaLavorazione', v === '__all__' ? '' : v)}>
              <SelectTrigger className="w-[200px] h-9" data-testid="tipo-select">
                <SelectValue placeholder="Tutti" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutti</SelectItem>
                {TIPOLOGIE_LAVORAZIONE.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Filtro Studio Progettazione (solo HQ e Fastweb) */}
          {showStudioFilter && uniqueStudi.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-fw-subtle">Studio Progettazione</label>
              <Select value={filters.studioProgettazione || '__all__'} onValueChange={(v) => updateFilter('studioProgettazione', v === '__all__' ? '' : v)}>
                <SelectTrigger className="w-[220px] h-9" data-testid="studio-select">
                  <SelectValue placeholder="Tutti" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Tutti</SelectItem>
                  {uniqueStudi.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Filtro Ditta — nascosto al ruolo Ditta (auto-filtrato dal DataContext) */}
          {showDittaFilter && uniqueDitte.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-fw-subtle">Ditta</label>
              <Select value={filters.ditta || '__all__'} onValueChange={(v) => updateFilter('ditta', v === '__all__' ? '' : v)}>
                <SelectTrigger className="w-[200px] h-9" data-testid="ditta-select">
                  <SelectValue placeholder="Tutte" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Tutte</SelectItem>
                  {uniqueDitte.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Filtro Stato Lavorazione */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-fw-subtle">Stato Lavorazione</label>
            <Select value={filters.workflowStatus || '__all__'} onValueChange={(v) => updateFilter('workflowStatus', v === '__all__' ? '' : v)}>
              <SelectTrigger className="w-[180px] h-9" data-testid="stato-select">
                <SelectValue placeholder="Tutti" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutti</SelectItem>
                {Object.entries(STATO_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Filtro Controllore documenti (solo HQ e Fastweb) */}
          {showStudioFilter && (
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-fw-subtle flex items-center gap-1">
                Controllore documenti
                <span
                  className="inline-flex items-center cursor-help"
                  title="Le pratiche dei professionisti interni HQ sono approvate da Fastweb invece di HQ (HQ non può controllare sé stesso). Usa questo filtro per isolare le pratiche che devi processare."
                  aria-label="Info controllore documenti"
                >
                  <Info className="w-3.5 h-3.5 text-fw-subtle" />
                </span>
              </label>
              <Select value={filters.fornitoreIsInternalHq || '__all__'} onValueChange={(v) => updateFilter('fornitoreIsInternalHq', v === '__all__' ? '' : v)}>
                <SelectTrigger className="w-[160px] h-9" data-testid="fornitore-interno-select">
                  <SelectValue placeholder="Tutti" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Tutti</SelectItem>
                  <SelectItem value="no">HQ</SelectItem>
                  <SelectItem value="yes">Fastweb</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Filtro Categoria Rifiuto */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-fw-subtle flex items-center gap-1">
              Categoria Rifiuto
              <span
                className="inline-flex items-center cursor-help"
                title="Mostra solo le pratiche il cui ultimo rifiuto documenti include la categoria selezionata."
                aria-label="Info categoria rifiuto"
              >
                <Info className="w-3.5 h-3.5 text-fw-subtle" />
              </span>
            </label>
            <Select value={filters.categoriaRifiuto || '__all__'} onValueChange={(v) => updateFilter('categoriaRifiuto', v === '__all__' ? '' : v)}>
              <SelectTrigger className="w-[200px] h-9" data-testid="categoria-rifiuto-select">
                <SelectValue placeholder="Tutte" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutte</SelectItem>
                {CATEGORIE_RIFIUTO.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Filtro Errori (rifiuti totali per pratica) */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-fw-subtle flex items-center gap-1">
              Errori
              <span
                className="inline-flex items-center cursor-help"
                title="Filtra per numero totale di rifiuti ricevuti dalla pratica (documenti + preventivo)."
                aria-label="Info errori"
              >
                <Info className="w-3.5 h-3.5 text-fw-subtle" />
              </span>
            </label>
            <Select value={filters.minErrori || '__all__'} onValueChange={(v) => updateFilter('minErrori', v === '__all__' ? '' : v)}>
              <SelectTrigger className="w-[150px] h-9" data-testid="min-errori-select">
                <SelectValue placeholder="Tutte" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutte</SelectItem>
                {Object.entries(MIN_ERRORI_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Filtro Periodo */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-fw-subtle">Periodo</label>
            <div className="flex items-center gap-1.5">
              <input
                type="date"
                value={filters.periodoDa}
                onChange={(e) => updateFilter('periodoDa', e.target.value)}
                max={filters.periodoA || undefined}
                className="h-9 w-[140px] rounded-md border border-fw-border-strong px-2 text-sm shadow-sm focus:border-fw-yellow focus:ring-1 focus:ring-fw-yellow outline-none"
                data-testid="periodo-da"
              />
              <span className="text-xs text-fw-subtle">—</span>
              <input
                type="date"
                value={filters.periodoA}
                onChange={(e) => updateFilter('periodoA', e.target.value)}
                min={filters.periodoDa || undefined}
                className="h-9 w-[140px] rounded-md border border-fw-border-strong px-2 text-sm shadow-sm focus:border-fw-yellow focus:ring-1 focus:ring-fw-yellow outline-none"
                data-testid="periodo-a"
              />
            </div>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={handleReset}
            className="text-fw-muted hover:text-fw-dark h-9"
            data-testid="reset-filters-btn"
          >
            <X className="w-4 h-4 mr-1" />
            Reset
          </Button>
        </div>
      )}
    </div>
  );
}
